
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import UploadSection from '@/components/UploadSection';
import AnalysisForm from '@/components/AnalysisForm';
import ResultsSection, { AnalysisResult } from '@/components/ResultsSection';
import { Separator } from '@/components/ui/separator';
import { analyzeBookCover } from '@/services/openai';
import { useToast } from '@/components/ui/use-toast';
import { BookOpen } from 'lucide-react';

const Index = () => {
  const [file, setFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const { toast } = useToast();

  const handleImageUploaded = (uploadedFile: File, url: string) => {
    setFile(uploadedFile);
    setImageUrl(url);
    // Reset any previous analysis
    setAnalysisResult(null);
    
    // Show a fun toast
    toast({
      title: "Book Cover Uploaded!",
      description: "Let's see if your cover is a bestseller or needs work...",
    });
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Extract the base64 part from the data URL
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        } else {
          reject(new Error('Failed to convert image to base64'));
        }
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleAnalyze = async (prompt: string) => {
    if (!file) {
      toast({
        title: "Wait a sec!",
        description: "Please upload an image first",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const base64Image = await convertToBase64(file);
      const result = await analyzeBookCover(base64Image, prompt);
      setAnalysisResult(result);
      
      // Show a fun toast based on the rating
      if (result.overallRating >= 8) {
        toast({
          title: "Wow! Great Cover!",
          description: `Your cover scored a ${result.overallRating}/10!`,
        });
      } else if (result.overallRating >= 5) {
        toast({
          title: "Not Bad!",
          description: `Your cover scored a ${result.overallRating}/10 with room for improvement.`,
        });
      } else {
        toast({
          title: "Needs Work...",
          description: `Your cover scored a ${result.overallRating}/10. Check out our suggestions!`,
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error("Analysis error:", error);
      toast({
        title: "Analysis Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup function for image URLs
  useEffect(() => {
    return () => {
      // Revoke any object URLs to avoid memory leaks
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-bookCream/30">
      <Header />
      
      <main className="flex-1 container mx-auto py-8">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <div className="inline-block mb-6 animate-bounce">
            <BookOpen className="h-16 w-16 text-bookRed" />
          </div>
          <h2 className="text-4xl font-serif font-bold mb-4 bg-gradient-to-r from-bookNavy via-bookRed to-bookNavy bg-clip-text text-transparent">
            Does My Book Cover Suck?
          </h2>
          <p className="text-bookGray max-w-2xl mx-auto text-lg leading-relaxed">
            Upload your book cover and get honest feedback on design, marketability, 
            genre clarity, and more. Find out if your cover is helping or hurting your sales!
          </p>
        </div>

        <Separator className="my-8 bg-gradient-to-r from-transparent via-bookGold/50 to-transparent h-0.5" />
        
        <UploadSection 
          onImageUploaded={handleImageUploaded} 
          isLoading={isLoading} 
        />
        
        {imageUrl && (
          <AnalysisForm 
            onSubmit={handleAnalyze} 
            isLoading={isLoading}
            isImageUploaded={!!imageUrl}
          />
        )}

        {analysisResult && (
          <>
            <Separator className="my-8 bg-gradient-to-r from-transparent via-bookGold/50 to-transparent h-0.5" />
            <ResultsSection result={analysisResult} imageUrl={imageUrl} />
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default Index;
